import React from 'react';
import { StandardTableActions } from '../../components/ui';

import { Column } from '../../components/DataTable/DataTable';
import { CustomField } from './useData';
import { formatDate as formatDateGlobal, APP_CONFIG } from '../../config/appConfig';
import { Badge } from '../../components/ui';
import { FieldConfig } from '../../components/generic/fieldRenderers';
import { FormLayout } from '../../components/generic/FormLayoutRenderer';
import { ThemeColors } from '../../contexts/ThemeContext';

// Module Metadata
export const moduleConfig = {
  // Module Information
  module: {
    title: 'Custom Fields',
    description: 'Manage custom fields for entities',
    permissionModuleKey: 'custom_fields',
    route: {
      list: '/custom-fields',
      new: '/custom-fields/new',
      detail: (id: number) => `/custom-fields/${id}`,
      edit: (id: number) => `/custom-fields/${id}/edit`,
    },
    // API Endpoints
    api: {
      list: '/custom-fields',
      detail: (id: number) => `/custom-fields/${id}`,
    },
  },

  // Pagination Settings
  pagination: {
    initialItemsPerPage: APP_CONFIG.table.defaultPageSize,
    itemsPerPageOptions: APP_CONFIG.table.pageSizeOptions,
  },

  // Search Settings
  search: {
    placeholder: 'Search custom fields by name, label, or entity type...',
  },

  // Filter Groups Configuration
  filterGroups: [
    {
      name: 'basic',
      label: 'Basic',
      icon: '🔍',
      fields: ['entityType', 'fieldType', 'isRequired'],
    },
  ],

  // Filter Configuration
  filters: {
    fields: [
      {
        key: 'entityType',
        label: 'Entity Type',
        type: 'select' as const,
        placeholder: 'Select entity type',
        options: [
          { value: 'Client', label: 'Client' },
          { value: 'Task', label: 'Task' },
          { value: 'Ticket', label: 'Ticket' },
          { value: 'Lead', label: 'Lead' },
          { value: 'Note', label: 'Note' },
          { value: 'Todo', label: 'Todo' },
          { value: 'Invoice', label: 'Invoice' },
          { value: 'ClientContact', label: 'Client Contact' },
          { value: 'Project', label: 'Project' },
          { value: 'Package', label: 'Package' },
        ],
      },
      {
        key: 'fieldType',
        label: 'Field Type',
        type: 'select' as const,
        placeholder: 'Select field type',
        options: [
          { value: 'text', label: 'Text' },
          { value: 'number', label: 'Number' },
          { value: 'date', label: 'Date' },
          { value: 'select', label: 'Select' },
          { value: 'checkbox', label: 'Checkbox' },
          { value: 'textarea', label: 'Textarea' },
        ],
      },
      {
        key: 'isRequired',
        label: 'Required',
        type: 'select' as const,
        placeholder: 'Select required status',
        options: [
          { value: 'true', label: 'Required' },
          { value: 'false', label: 'Optional' },
        ],
      },
    ],
    // Filter logic function
    applyFilter: (
      items: CustomField[],
      filters: {
        entityType?: string[];
        fieldType?: string[];
        isRequired?: string[];
      }
    ) => {
      return items.filter((item) => {
        // Entity type filter
        if (filters.entityType && filters.entityType.length > 0) {
          if (!filters.entityType.includes(item.entityType)) return false;
        }

        // Field type filter
        if (filters.fieldType && filters.fieldType.length > 0) {
          if (!filters.fieldType.includes(item.fieldType)) return false;
        }

        // Required filter
        if (filters.isRequired && filters.isRequired.length > 0) {
          const isRequiredStr = item.isRequired.toString();
          if (!filters.isRequired.includes(isRequiredStr)) return false;
        }

        return true;
      });
    },
  },

  // Export Configuration
  export: {
    enabled: true,
    formats: ['csv', 'excel', 'pdf'],
  },

  // Detail View Configuration
  detail: {
    subtitle: 'Custom Field Details',
    notFoundTitle: 'Custom field not found',
    notFoundMessage: 'The custom field you\'re looking for doesn\'t exist.',
    notFoundBackText: 'Back to Custom Fields',
    defaultTitle: 'Unnamed Custom Field',
    getTitle: (item: CustomField) => {
      return item.fieldLabel || `Custom Field #${item.id}`;
    },
  },

  // Unified Field Configuration
  fields: [
    {
      name: 'entityType',
      label: 'Entity Type',
      type: 'select',
      required: true,
      placeholder: 'Select entity type',
      options: [
        { value: 'Client', label: 'Client' },
        { value: 'Task', label: 'Task' },
        { value: 'Ticket', label: 'Ticket' },
        { value: 'Lead', label: 'Lead' },
        { value: 'Note', label: 'Note' },
        { value: 'Todo', label: 'Todo' },
        { value: 'Invoice', label: 'Invoice' },
        { value: 'ClientContact', label: 'Client Contact' },
        { value: 'Project', label: 'Project' },
        { value: 'Package', label: 'Package' },
      ],
    },
    {
      name: 'fieldName',
      label: 'Field Name',
      type: 'text',
      required: true,
      placeholder: 'e.g., customField1',
      description: 'Unique identifier for the field (lowercase, no spaces)',
      validation: {
        required: true,
        minLength: 2,
        maxLength: 255,
      },
    },
    {
      name: 'fieldLabel',
      label: 'Field Label',
      type: 'text',
      required: true,
      placeholder: 'e.g., Custom Field 1',
      description: 'Display name for the field',
      validation: {
        required: true,
        minLength: 2,
        maxLength: 255,
      },
    },
    {
      name: 'fieldType',
      label: 'Field Type',
      type: 'select',
      required: true,
      placeholder: 'Select field type',
      options: [
        { value: 'text', label: 'Text' },
        { value: 'number', label: 'Number' },
        { value: 'date', label: 'Date' },
        { value: 'select', label: 'Select (Dropdown)' },
        { value: 'checkbox', label: 'Checkbox' },
        { value: 'textarea', label: 'Textarea' },
      ],
    },
    {
      name: 'isRequired',
      label: 'Required',
      type: 'switch',
      switchLabel: 'This field is required',
      labelPosition: 'left',
      defaultValue: false,
    },
    {
      name: 'order',
      label: 'Order',
      type: 'number',
      placeholder: '0',
      description: 'Display order (lower numbers appear first)',
    },
  ] as FieldConfig[],

  // Form Layout Configuration (crm-tasks style: gridCols + fieldSpans)
  formLayout: {
    mode: 'default',
    sections: [
      {
        id: 'basic',
        title: 'Basic Information',
        icon: '📅',
        gridCols: 'md:grid-cols-12',
        fields: ['entityType', 'fieldName', 'fieldLabel', 'fieldType', 'isRequired', 'order'],
        fieldSpans: {
          entityType: 'md:col-span-6',
          fieldName: 'md:col-span-6',
          fieldLabel: 'md:col-span-6',
          fieldType: 'md:col-span-6',
          isRequired: 'md:col-span-6',
          order: 'md:col-span-6',
        },
        collapsible: true,
      },
    ],
  } as FormLayout,

  // Toolbar Actions Configuration
  toolbar: {
    actions: [
      {
        key: 'download',
        icon: 'download',
        title: 'Download',
        showTitle: false,
        variant: 'default' as const,
        dropdown: [
          {
            key: 'export-excel',
            label: 'Export to Excel',
            icon: 'download',
            onClick: async (exportData?: {
              sortedData: any[];
              tableColumns: any[];
              entityName: string;
              exportFileName: string;
              formatDate?: (dateString?: string) => string;
            }) => {
              if (!exportData) {
                console.error('Export data not provided');
                return;
              }

              try {
                const { exportToExcel } = await import('../../components/exports');
                await exportToExcel(exportData);
              } catch (error: any) {
                console.error('Failed to export to Excel:', error);
                throw new Error(error?.message || 'Failed to export to Excel. Please check the console for details.');
              }
            },
          },
        ],
      },
      {
        key: 'add',
        icon: 'add',
        title: 'Add New Custom Field',
        showTitle: false,
        variant: 'primary' as const,
        href: '/custom-fields/new',
      },
    ],
  },

  // Table Configuration
  table: {
    actionsPosition: 'left' as const,
    onRowClick: (item: CustomField) => {
      window.location.href = `/custom-fields/${item.id}`;
    },
    keyExtractor: (item: CustomField) => item.id,
  },

  // Empty States
  emptyStates: {
    noData: 'No custom fields found. Create your first custom field to get started.',
    noSearchResults: 'No custom fields match your search criteria.',
    noFilterResults: 'No custom fields match your filter criteria.',
  },

  // Delete Confirmation
  delete: {
    title: 'Delete Custom Field',
    message: 'Are you sure you want to delete this custom field? This action cannot be undone.',
    confirmText: 'Delete',
    cancelText: 'Cancel',
    errorMessage: 'Failed to delete custom field. Please try again.',
    handler: async (id: number) => {
      const { deleteCustomField } = await import('./useData');
      await deleteCustomField(id);
    },
  },

  // Utility Functions
  utils: {
    formatDate: (dateString?: string) => {
      if (!dateString) return '-';
      return formatDateGlobal(dateString);
    },
  },

  // Form Configuration
  form: {
    new: {
      title: 'New Custom Field',
      subtitle: 'Add a new custom field to your database',
      submitText: 'Create Custom Field',
    },
    edit: {
      title: 'Edit Custom Field',
      submitText: 'Update Custom Field',
    },
    cancelText: 'Cancel',
    loadingText: 'Loading custom field...',
    errorMessage: 'Failed to save custom field. Please try again.',
    create: async (data: Partial<CustomField>) => {
      const { createCustomField } = await import('./useData');
      return await createCustomField(data);
    },
    update: async (id: number, data: Partial<CustomField>) => {
      const { updateCustomField } = await import('./useData');
      return await updateCustomField(id, data);
    },
  },
};

// Helper function to get columns
export const getColumns = (
  formatDate: (dateString?: string) => string,
  colors?: ThemeColors
): Column<CustomField>[] => {
  const textColor = colors?.text || 'text-gray-900';
  const warningColor = colors?.warning || 'bg-amber-50 text-amber-800';
  const defaultBg = colors?.surfaceHover || 'bg-gray-100';
  const defaultText = colors?.textSecondary || 'text-gray-800';
  
  return [
  {
    key: 'fieldLabel',
    header: 'Label',
    sortable: true,
    render: (value) => (
      <span className={`text-sm ${textColor} font-medium`}>{value || '-'}</span>
    ),
  },
  {
    key: 'fieldName',
    header: 'Field Name',
    sortable: true,
    render: (value) => (
      <span className={`text-sm ${textColor} font-mono`}>{String(value)}</span>
    ),
  },
  {
    key: 'entityType',
    header: 'Entity Type',
    sortable: true,
    render: (value) => (
      <span className={`text-sm ${textColor}`}>{String(value)}</span>
    ),
  },
  {
    key: 'fieldType',
    header: 'Type',
    sortable: true,
    render: (value) => (
      <span className={`text-sm ${textColor}`}>{String(value)}</span>
    ),
  },
  {
    key: 'isRequired',
    header: 'Required',
    sortable: true,
    render: (value) => {
      const statusColor = value ? warningColor : `${defaultBg} ${defaultText}`;
      const [bg, text] = statusColor.split(' ');
      return (
        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${bg} ${text}`}>
          {value ? 'Yes' : 'No'}
        </span>
      );
    },
  },
  {
    key: '_count',
    header: 'Values',
    sortable: false,
    render: (value, row) => (
      <span className={`text-sm ${textColor}`}>{(row as any)._count?.values || 0}</span>
    ),
  },
  {
    key: 'order',
    header: 'Order',
    sortable: true,
    render: (value) => (
      <span className={`text-sm ${textColor}`}>{String(value)}</span>
    ),
  },
  ];
};

// Helper function to render toolbar icon
export const getToolbarIcon = (iconName: string): React.ReactNode => {
  switch (iconName) {
    case 'download':
      return (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
        </svg>
      );
    case 'add':
      return (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
        </svg>
      );
    default:
      return null;
  }
};

// Helper function to render table action buttons
export const getTableActions = (
  item: CustomField,
  onDelete: (id: number) => void,
  _colors?: unknown,
  _listContext?: unknown,
  options?: { canDelete?: boolean; canEdit?: boolean }
): React.ReactNode => {
  const canDelete = options?.canDelete !== false;
  const canEdit = options?.canEdit !== false;
  return (
    <StandardTableActions
      viewTo={moduleConfig.module.route.detail(item.id)}
      editTo={moduleConfig.module.route.edit(item.id)}
      onDelete={() => onDelete(item.id)}
      canEdit={canEdit}
      canDelete={canDelete}
    />
  );
};


export const getStatusBadges = (item: CustomField, colors?: ThemeColors) => {
  const warningColor = colors?.warning || 'bg-amber-50 text-amber-800';
  const infoColor = colors?.info || 'bg-sky-50 text-sky-800';
  
  return (
    <>
      {item.isRequired && (
        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${warningColor}`}>
          Required
        </span>
      )}
      {item.fieldType && (
        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${infoColor}`}>
          {item.fieldType}
        </span>
      )}
    </>
  );
};

// Helper function to get detail sections
export const getDetailSections = (
  item: CustomField,
  formatDate: (dateString?: string) => string,
  _formatCurrency?: (amount?: number | null) => string,
  _mutate?: () => void,
  colors?: ThemeColors
) => {
  const textMuted = colors?.textMuted || 'text-gray-500';
  const surfaceHover = colors?.surfaceHover || 'bg-gray-50';
  const border = colors?.border || 'border';
  
  return [
    {
      title: 'Basic Information',
      icon: '📅',
      fields: [
        {
          label: 'Field Label',
          value: item.fieldLabel,
        },
        {
          label: 'Field Name',
          value: <span className="font-mono text-sm">{item.fieldName}</span>,
        },
        {
          label: 'Entity Type',
          value: <Badge variant="info">{item.entityType}</Badge>,
        },
        {
          label: 'Field Type',
          value: <Badge variant="secondary">{item.fieldType}</Badge>,
        },
        {
          label: 'Required',
          value: (
            <Badge variant={item.isRequired ? 'warning' : 'secondary'}>
              {item.isRequired ? 'Yes' : 'No'}
            </Badge>
          ),
        },
        {
          label: 'Order',
          value: item.order,
        },
      ],
    },
    {
      title: 'Options',
      icon: '⏰',
      fields: [
        {
          label: '',
          value: item.options && Array.isArray(item.options) && item.options.length > 0 ? (
            <div className="space-y-2">
              {item.options.map((option, index) => (
                <Badge key={index} variant="secondary">{String(option)}</Badge>
              ))}
            </div>
          ) : (
            <span className={textMuted}>No options defined</span>
          ),
        },
      ],
    },
    {
      title: 'Validation Rules',
      icon: '📋',
      fields: [
        {
          label: '',
          value: item.validation ? (
            <pre className={`text-xs ${surfaceHover} p-3 rounded ${border} overflow-auto`}>
              {JSON.stringify(item.validation, null, 2)}
            </pre>
          ) : (
            <span className={textMuted}>No validation rules defined</span>
          ),
        },
      ],
    },
  ];
};

// Helper function to get initial form data
export const getInitialFormData = (): Partial<CustomField> => ({
  entityType: '',
  fieldName: '',
  fieldLabel: '',
  fieldType: 'text',
  isRequired: false,
  order: 0,
  options: [],
  validation: null,
});

// Helper function to map item data to form data
export const mapItemToFormData = (item: CustomField): Partial<CustomField> => ({
  entityType: item.entityType,
  fieldName: item.fieldName,
  fieldLabel: item.fieldLabel,
  fieldType: item.fieldType,
  isRequired: item.isRequired,
  order: item.order,
  options: item.options || [],
  validation: item.validation || null,
});

export type ModuleConfig = typeof moduleConfig;
